import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { stageLevels, getStageForGender } from "@/lib/avatarData";
import { useUserProfile } from "@/hooks/useUserProfile";

// Gate engine tables/functions ship with this release; cast until generated types include them.
const db = supabase as any;

export interface GateItem {
  id: string;
  label: string;
  source: string;
  operator: ">=" | "<=";
  threshold: number;
  unlocks: string | null;
  current: number | null;
  met: boolean;
}

export interface GateStatus {
  current_stage: number;
  next_stage: number;
  items: GateItem[];
  values: Record<string, number | null>;
  advanced: boolean;
}

export function useGateStatus() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: profile } = useUserProfile();
  const q = useQuery({
    queryKey: ["gate-status", user?.id],
    enabled: !!user,
    retry: false,
    queryFn: async (): Promise<GateStatus> => {
      const { data, error } = await db.rpc("evaluate_member_gate", { _user: user!.id });
      if (error) throw error;
      return data as GateStatus;
    },
  });

  useEffect(() => {
    if (q.data?.advanced) {
      const lvl = stageLevels[q.data.current_stage - 1];
      const name = lvl ? getStageForGender(lvl, profile?.gender === "female" ? "female" : "male").name : `Stage ${q.data.current_stage}`;
      toast({ title: `Gate crossed: ${name}`, description: "Well done. Your coach will review this step." });
      qc.invalidateQueries({ queryKey: ["stage-advancement"] });
      qc.invalidateQueries({ queryKey: ["gate-status", user?.id] });
    }
  }, [q.data?.advanced]);

  return q;
}

export function formatGateCurrent(item: GateItem) {
  if (item.current == null) return "not logged";
  const money = ["cards_debt", "total_debt", "savings", "net_worth"].includes(item.source);
  if (money) return `$${Math.round(Number(item.current)).toLocaleString()}`;
  if (item.source === "desk_rank") return ["Intern", "Intern", "Analyst", "Trader", "Senior Trader"][Number(item.current)] ?? "Intern";
  if (item.source === "researcher_academy") return Number(item.current) >= 1 ? "done" : "in progress";
  if (item.source === "body_fat") return `${Number(item.current).toFixed(1)}%`;
  return `${Math.round(Number(item.current))}`;
}
